/**
 * Types of social media to share on
 */
export type SocialShareType = 'facebook' | 'twitter' | 'x' | 'linkedin' | 'whatsapp';

/**
 * Options for sharing on social media
 */
export interface SocialShareOptions {
  /**
   * The content to share (e.g. a message)
   */
  content?: string;
  /**
   * The URL to share
   */
  url?: string;
  /**
   * The hashtags to include in the share on twitter/x or facebook
   * Obs: On facebook only the first hashtag is used
   */
  hashtags?: string[];
  /**
   * The twitter handle to include in the share (if not provided, the current user's handle is used)
   */
  via?: string;
  /**
   * The twitter handles to include as related in the share
   */
  related?: string[];
}

/**
 * Share on social media
 * @param type The type of social media to share on
 * @param options The options for sharing on social media
 * @throws Error if type is twitter and the tweet(content + url + hastags + via + related) is longer than 280 characters
 */
export function shareOnSocial<T extends SocialShareType>(type: T, options: SocialShareOptions) {
  console.log('shareOnSocial', type, options);
  switch (type) {
    case 'facebook':
      shareOnFacebook(options);
      break;
    case 'twitter':
      shareOnX(options);
      break;
    case 'x':
      shareOnX(options);
      break;
    case 'linkedin':
      shareOnLinkedIn(options);
      break;
    case 'whatsapp':
      shareOnWhatsApp(options);
      break;
    default:
      console.error('Unsupported social share type');
  }
}

/**
 * Calculate the size of a post on X
 * @param options The options for sharing on social media
 * @returns The size of the post (content + url + hastags + via + related)
 */
export function calculateXPostSize(options: SocialShareOptions) {
  let size = 0;
  if (options.content) {
    size += options.content.length;
  }
  if (options.url) {
    size += options.url.length;
  }
  if (options.hashtags) {
    size += options.hashtags.join(',').length;
  }
  if (options.via) {
    size += options.via.length;
  }
  if (options.related) {
    size += options.related.join(',').length;
  }
  return size;
}

function shareOnX(options: SocialShareOptions) {
  if (calculateXPostSize(options) > 280) {
    throw new Error('Tweet is too long, max size is 280 characters');
  }
  let url = 'https://www.x.com/intent/tweet?';
  if (options.content) {
    url = `${url}text=${encodeURIComponent(options.content)}&`;
  }
  if (options.url) {
    url = `${url}url=${encodeURIComponent(options.url)}&`;
  }
  if (options.hashtags) {
    url = `${url}hashtags=${encodeURIComponent(options.hashtags.join(','))}&`;
  }
  if (options.via) {
    url = `${url}via=${encodeURIComponent(options.via)}&`;
  }
  if (options.related) {
    url = `${url}related=${encodeURIComponent(options.related.join(','))}&`;
  }
  window.open(url, '_blank');
}

function shareOnWhatsApp(options: SocialShareOptions) {
  let url = 'https://wa.me/?';
  if (options.content) {
    url = `${url}text=${encodeURIComponent(options.content)}&`;
  }
  url = `${url}type=custom_url&`;

  window.open(url, '_blank');
}

function shareOnLinkedIn(options: SocialShareOptions) {
  let url = 'https://www.linkedin.com/feed/?shareActive=true&';
  if (options.content) {
    url = `${url}text=${encodeURIComponent(options.content)}&`;
  }
  if (options.url) {
    url = `${url}shareUrl=${encodeURIComponent(options.url)}&`;
  }
  window.open(url, '_blank');
}

function shareOnFacebook(options: SocialShareOptions) {
  let url = 'https://www.facebook.com/dialog/share?display=page&';
  if (options.hashtags && options.hashtags.length > 0 && options.hashtags[0]) {
    url = `${url}hashtag=${encodeURIComponent(options.hashtags[0])}&`;
  }
  if (options.url) {
    url = `${url}href=${encodeURIComponent(options.url)}&`;
  }
  window.open(url, '_blank');
}
